import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Tenant, TenantService } from './tenant.service';

/** Clave que lee JwtInterceptor para enviar el header x-acting-tenant. */
export const ACTING_TENANT_KEY = 'acting_tenant';

function jwtPayload(): Record<string, unknown> | null {
  const token = localStorage.getItem('access_token');
  if (!token) return null;
  try {
    // JWT usa base64url: normalizar a base64 clásico y re-padear antes de atob.
    const seg = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(seg + '='.repeat((4 - (seg.length % 4)) % 4)));
  } catch {
    return null;
  }
}

/**
 * Contexto de tenant del backoffice (ADR-001, T8) — espejo del patrón de
 * VerticalContextService:
 * - Operador de PLATAFORMA (claim tenantId null): selector en el topbar; la
 *   selección viaja como header x-acting-tenant y el backend confina lecturas
 *   y escrituras a ese tenant. "Todos" = sin header.
 * - Operador de tenant: badge con el nombre de su tenant (el backend solo le
 *   devuelve su propia fila); el claim manda, el header se ignora.
 * El cambio recarga la app: con fetchPolicy no-cache y vistas realtime, es la
 * forma simple de garantizar que nada quede mezclado entre tenants.
 */
@Injectable({ providedIn: 'root' })
export class TenantContextService {
  private readonly _tenants = new BehaviorSubject<Tenant[]>([]);
  readonly tenants$ = this._tenants.asObservable();
  private _loaded = false;
  private _loading = false;

  constructor(private tenantService: TenantService) {}

  get tenants(): Tenant[] { return this._tenants.value; }
  get loaded(): boolean { return this._loaded; }

  /** true si el operador autenticado es de plataforma (claim tenantId null). */
  get isPlatform(): boolean {
    const payload = jwtPayload();
    return payload !== null && 'tenantId' in payload && payload['tenantId'] === null;
  }

  /** Tenant seleccionado por plataforma (null = todos). */
  get actingTenantId(): number | null {
    const raw = localStorage.getItem(ACTING_TENANT_KEY);
    const parsed = raw ? Number(raw) : NaN;
    return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
  }

  /** Nombre a mostrar: badge del operador tenant o selección de plataforma. */
  get currentName(): string {
    const acting = this.actingTenantId;
    if (this.isPlatform) {
      return this.tenants.find((t) => Number(t.id) === acting)?.name ?? '';
    }
    return this.tenants[0]?.name ?? '';
  }

  /** Mostrar algo en el topbar: selector (plataforma) o badge (tenant con nombre). */
  get visible(): boolean {
    return this.isPlatform ? this._loaded : !!this.currentName;
  }

  /**
   * Carga idempotente y REINTENTABLE: un fallo no la deja latcheada (el topbar
   * la reinvoca al emitir currentUser$, p.ej. tras el login o al restaurar
   * sesión), así el selector no desaparece para siempre por una query caída.
   */
  load(): void {
    if (this._loaded || this._loading || !localStorage.getItem('access_token')) return;
    this._loading = true;
    this.tenantService.getTenants().subscribe({
      next: (r) => {
        this._loading = false;
        this._loaded = true;
        this._tenants.next(r.nodes.filter((t) => t.status === 'ACTIVE'));
        // Selección guardada que ya no existe → limpiar para no filtrar a ciegas.
        if (this.isPlatform && this.actingTenantId !== null &&
            !r.nodes.some((t) => Number(t.id) === this.actingTenantId)) {
          localStorage.removeItem(ACTING_TENANT_KEY);
        }
      },
      error: (e) => {
        this._loading = false;
        console.warn('[TenantContext] no se pudo cargar tenants:', e?.message || e);
      },
    });
  }

  /** Cambia el tenant activo de plataforma (null = todos) y recarga la app. */
  select(tenantId: number | null): void {
    if (!this.isPlatform || tenantId === this.actingTenantId) return;
    if (tenantId === null) localStorage.removeItem(ACTING_TENANT_KEY);
    else localStorage.setItem(ACTING_TENANT_KEY, String(tenantId));
    window.location.reload();
  }
}
